import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import type { LookupGroup, LookupSelectionMode } from './lookupTypes';
import { formatGroupDisplayName, groupMatchesSearch } from './lookupUtils';

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const FETCH_LIMIT = 500;
const EMPTY_GROUPS: LookupGroup[] = [];

interface GroupLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: LookupSelectionMode;
  title?: string;
  /** When set, only groups matching these types are fetched from the API. */
  groupTypes?: string[];
  initialSelected?: LookupGroup[];
  onConfirm: (selected: LookupGroup[]) => void;
}

function mapApiGroup(raw: any): LookupGroup {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description,
    type: raw.type,
  };
}

const GroupLookupModal: React.FC<GroupLookupModalProps> = ({
  isOpen,
  onClose,
  mode,
  title = 'Groups',
  groupTypes,
  initialSelected = EMPTY_GROUPS,
  onConfirm,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<LookupGroup[]>(initialSelected);

  // Reset only when the modal opens — unstable [] default would clear typed search.
  useEffect(() => {
    if (!isOpen) return;
    setSelected(initialSelected);
    setSearchInput('');
    setAppliedSearch('');
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally open-only
  }, [isOpen]);

  const typesKey = groupTypes?.length ? groupTypes.join(',') : 'all';

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['lookup-groups', typesKey],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(FETCH_LIMIT),
        page: '1',
      });
      if (groupTypes?.length) {
        params.set('types', groupTypes.join(','));
      }
      const response = await api.get(`/groups?${params.toString()}`);
      const groups = response.data.groups || response.data || [];
      return groups.map(mapApiGroup) as LookupGroup[];
    },
    enabled: isOpen,
    staleTime: 60000,
  });

  const filteredGroups = useMemo(() => {
    const all = data || [];
    return all.filter((g) => groupMatchesSearch(g, appliedSearch));
  }, [data, appliedSearch]);

  const total = filteredGroups.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageGroups = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredGroups.slice(start, start + pageSize);
  }, [filteredGroups, page, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setPage(1);
  };

  const isSelected = (id: string) => selected.some((g) => g.id === id);

  const toggleGroup = (group: LookupGroup) => {
    if (mode === 'single') {
      setSelected([group]);
      return;
    }
    setSelected((prev) =>
      prev.some((g) => g.id === group.id)
        ? prev.filter((g) => g.id !== group.id)
        : [...prev, group]
    );
  };

  const handleSelectAllPage = (checked: boolean) => {
    if (mode === 'single') return;
    if (checked) {
      const merged = [...selected];
      pageGroups.forEach((group) => {
        if (!merged.some((g) => g.id === group.id)) merged.push(group);
      });
      setSelected(merged);
    } else {
      const pageIds = new Set(pageGroups.map((g) => g.id));
      setSelected((prev) => prev.filter((g) => !pageIds.has(g.id)));
    }
  };

  const allPageSelected =
    mode === 'multi' && pageGroups.length > 0 && pageGroups.every((g) => isSelected(g.id));
  const somePageSelected =
    mode === 'multi' && pageGroups.some((g) => isSelected(g.id)) && !allPageSelected;

  const handleConfirm = () => {
    onConfirm(mode === 'single' ? selected.slice(0, 1) : selected);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">{title}</Dialog.Title>
                  <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-4 py-3 border-b border-gray-100">
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search groups..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>
                    <button type="submit" className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Search
                    </button>
                  </form>
                  {mode === 'multi' && selected.length > 0 && (
                    <p className="mt-2 text-xs text-indigo-700">{selected.length} group(s) selected</p>
                  )}
                </div>

                <div className="flex-1 overflow-auto min-h-[240px]">
                  {isLoading ? (
                    <div className="p-8 text-center text-gray-500 text-sm">Loading groups...</div>
                  ) : pageGroups.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 text-sm">No groups found</div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 w-10">
                            {mode === 'multi' && (
                              <input
                                type="checkbox"
                                checked={allPageSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = somePageSelected;
                                }}
                                onChange={(e) => handleSelectAllPage(e.target.checked)}
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                aria-label="Select all on page"
                              />
                            )}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {pageGroups.map((group) => {
                          const checked = isSelected(group.id);
                          return (
                            <tr
                              key={group.id}
                              className={`cursor-pointer hover:bg-gray-50 ${checked ? 'bg-indigo-50/50' : ''}`}
                              onClick={() => toggleGroup(group)}
                            >
                              <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type={mode === 'single' ? 'radio' : 'checkbox'}
                                  name="group-lookup"
                                  checked={checked}
                                  onChange={() => toggleGroup(group)}
                                  className="h-4 w-4 text-indigo-600 border-gray-300"
                                  aria-label={`Select ${formatGroupDisplayName(group)}`}
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">{formatGroupDisplayName(group)}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{group.type || '—'}</td>
                              <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate">{group.description || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="px-4 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <span>Show</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="border border-gray-300 rounded px-2 py-1"
                    >
                      {PAGE_SIZE_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <span>
                      {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                    </span>
                    {isFetching && !isLoading && <span className="text-xs text-gray-400">Updating...</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-2 text-gray-600">Page {page} of {totalPages}</span>
                    <button
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>

                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={selected.length === 0}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {mode === 'single' ? 'Select' : `Add (${selected.length})`}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GroupLookupModal;
